<template>
  <div
    id="app"
    class="POS-r"
  >
    <loader v-if="showLoader"></loader>
    <router-view/>
  </div>
</template>

<script>
import Fingerprint2 from 'fingerprintjs2';
import Axios from '@/helpers/axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { mapState, mapMutations } from 'vuex';

import loader from '@/components/UI/Loader.vue';

export default {
  name: 'APP',
  components: {
    loader,
  },
  beforeMount() {
    this.$router.onReady(() => {
      const liffPromise = new Promise((resolve) => {
        if (this.$route.query.liff && !window.liff) {
          const liffScript = document.createElement('script');
          liffScript.src = 'https://static.line-scdn.net/liff/edge/versions/2.3.1/sdk.js';
          liffScript.charset = 'utf-8';
          liffScript.onload = () => {
            window.liff.ready.then(() => {
              if (window.liff.isLoggedIn()) {
                this.setLactk(window.liff.getAccessToken());
              } else {
                window.liff.login();
              }
              resolve();
            });
            window.liff.init({ liffId: this.$route.query.liff });
          };
          document.head.appendChild(liffScript);
        } else {
          resolve();
        }
      });

      liffPromise.then(() => {
        Fingerprint2.get((components) => {
          const values = components.map((component) => component.value);
          const fingerprint = Fingerprint2.x64hash128(values.join(''), 31);

          const data = {
            ...this.$route.query,
            brand_id: this.$route.params.brandId,
            device_name: this.client.deviceName,
            os: this.client.os,
            fingerprint,
          };

          if (this.client.dtk) {
            data.dtk = this.client.dtk;
          }

          if (this.client.lactk) {
            data.line_access_token = this.client.lactk;
          }

          Axios({
            method: 'post',
            url: '/login/registerDevice',
            data,
          })
            .then((response) => {
              if (response.data.code !== 500) {
                if (response.data.msg) {
                  Swal.fire({
                    text: response.data.msg,
                  })
                    .then(() => {
                      if (response.data.clearOrder) {
                        this.REMOVE_FROM_SAVED_ORDERS({
                          brand: this.$route.params.brandId,
                          store: this.$route.params.storeId,
                        });
                        this.RESET_CART();
                      }

                      if (response.data.reload) {
                        window.location.reload();
                      }

                      if (response.data.redirect) {
                        this.$router.push(response.data.redirect);
                      }
                    });
                } else {
                  if (response.data.clearOrder) {
                    this.REMOVE_FROM_SAVED_ORDERS({
                      brand: this.$route.params.brandId,
                      store: this.$route.params.storeId,
                    });
                    this.RESET_CART();
                  }

                  if (response.data.reload) {
                    window.location.reload();
                  }

                  if (response.data.redirect) {
                    this.$router.push(response.data.redirect);
                  }
                }
              }

              if (response.data.code === 500) {
                if (response.data.data.dtk) {
                  this.setDtk(response.data.data.dtk);
                }
                if (response.data.data.utk) {
                  this.setUtk(response.data.data.utk);
                }
                if (response.data.data.bot_pkg) {
                  this.setLtk(response.data.data.bot_pkg);
                }
                this.login();
              }
            });
        });
      });
    });

    document.documentElement.lang = this.$i18n.locale;
  },
  data() {
    return {
      showLoader: true,
    };
  },
  created() {
    this.$moment.locale(this.$i18n.locale);
  },
  watch: {
    $route() {
      if (!Cookies.get('ocard_order_lang')) {
        if (typeof this.$route.query.lang === 'string' && this.$route.query.lang.includes('en')) {
          this.$i18n.locale = 'en';
        }
      }
    },
    '$i18n.locale': function onLocaleChange(newLocale) {
      document.documentElement.lang = newLocale;
    },
    '$route.params.brandId': {
      handler() {
        if (this.$route.params.brandId) {
          this.$store.dispatch('GET_BRAND_INFO', {
            brandId: this.$route.params.brandId,
          });
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState([
      'client',
      'user',
    ]),
  },
  methods: {
    ...mapMutations([
      'setDtk',
      'setUtk',
      'setLactk',
      'setLtk',
      'SET_USER',
      'RESET_CART',
      'REMOVE_FROM_SAVED_ORDERS',
    ]),

    login() {
      if (!this.$route.params.brandId) {
        this.showLoader = false;
        return;
      }

      const data = {
        ...this.$route.query,
        brand_id: this.$route.params.brandId,
      };

      Axios({
        method: 'post',
        url: '/login/panel',
        data,
      })
        .then((response) => {
          this.SET_USER(response.data.data);
          if (this.user.cell !== '') {
            this.$Sentry.setUser({
              username: btoa(this.user.cell),
            });
          } else if (this.client.dtk) {
            this.$Sentry.setUser({
              username: btoa(this.client.dtk),
            });
          }
          this.showLoader = false;
        });
    },
  },
};
</script>

<style lang="sass">
@import url('//fonts.googleapis.com/css2?family=Montserrat:wght@300;500;600&family=Noto+Sans+TC:wght@400;700&family=Space+Mono&display=swap')
@import url('//fonts.googleapis.com/icon?family=Material+Icons|Material+Icons+Round')
// Bootstrap
@import '~bootstrap/scss/bootstrap-reboot'
@import '~bootstrap/scss/bootstrap-grid'

html,
body,
#app
  width: 100%
  height: 100%

  font-family: Montserrat, 'SF Pro TC', 'SF Pro HK', 'SF Pro SC', 'SF Pro Text', 'PingFang TC', 'PingFang HK', 'PingFang SC', 'Noto Sans TC', ui-sans-serif, system-ui, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'
  color: $color-dark
  -webkit-font-smoothing: antialiased
  -moz-osx-font-smoothing: grayscale

.section-title
  @extend .MB-s
  @extend .PR-m
  @extend .PL-m
  @extend .FW-b
  @extend .FZ-16
  @extend .LH-140

  letter-spacing: 1.5px

.section-sub-title
  @extend .MB-s
  @extend .PR-m
  @extend .PL-m
  @extend .FZ-14
  @extend .LH-140
  @extend .C-grey

.icon-button
  display: flex

  .icon
    @extend .MR-s

  .label
    flex-grow: 1
    @extend .PR-xl

.el-message
  top: 75% !important
  max-width: 80%
  min-width: auto
  padding: 12px 15px
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, .14), 0 3px 1px -2px rgba(0, 0, 0, .2), 0 1px 5px 0 rgba(0, 0, 0, .12)

  border-color: transparent
  border-radius: 10px

  &--success
    background-color: white

    font-weight: bold

    .el-message__content
      color: inherit

  &--error
    background-color: $color-notice

    .el-message__content
      color: white

  .el-message__content
    font-weight: bold

  .el-message__icon
    display: none

@keyframes showSweetAlert
  0%
    opacity: 0
    transform: scale(.75)

  100%
    opacity: 1
    transform: scale(1)

.swal2
  &-container
    &.swal2-backdrop-show
      background: rgba(0, 0, 0, .75) !important

  &-popup
    padding: 0 !important

    border-radius: $size-m !important

    background-color: #111 !important

    color: white !important

    &.swal2-show
      animation: showSweetAlert .25s $cb !important

    .swal2-content
      width: 100% !important
      padding: 32px 24px 16px !important
      margin: 0 !important

      font-size: 16px !important
      font-weight: bold !important
      color: white !important
      text-align: left !important
      white-space: pre-wrap !important

    .swal2-actions
      justify-content: flex-end !important
      padding: 8px 8px 8px 24px !important
      margin: 0 !important

    .swal2-styled
      &.swal2-confirm,
      &.swal2-cancel
        margin: 0 !important
        padding: 16px !important

        border-radius: 0 !important

        background: none !important

        font-size: 14px !important
        font-weight: 700 !important
        color: #ffea00 !important

        outline: none !important
        appearance: none !important

        &:focus
          box-shadow: none !important

        &:hover,
        &:active
          color: #ffea00 !important

          outline: none !important
          transform: scale(.95) !important

      &.swal2-cancel
        color: #656565 !important

input[type=search]::-webkit-search-cancel-button
  display: none

.grecaptcha-badge
  display: none !important

</style>
